/**
 * Skipp search intelligence — centralized configuration.
 *
 * Edit synonyms, ranking weights, and thresholds here.
 * Do not scatter these values through application code.
 *
 * Loaded as a classic script in the theme, and also require()-able in Node tests.
 */
(function (root) {
  'use strict';

  var SKIPP_SEARCH_CONFIG = {
    SEARCH_DEBOUNCE_MS: 300,
    CACHE_TTL_MS: 5 * 60 * 1000,
    CACHE_MAX_ENTRIES: 80,
    ANALYTICS_MAX_EVENTS: 200,
    LEARNED_TYPO_MIN_CLICKS: 1,

    TYPO_DISTANCE_THRESHOLD: 2,
    TYPO_MIN_TOKEN_LENGTH: 4,
    PREFIX_MIN_TOKEN_LENGTH: 3,
    JARO_WINKLER_MIN: 0.89,
    CORRECTION_CONFIDENCE_GAP: 0.04,
    COMPOUND_MIN_LENGTH: 8,

    MIN_RELEVANCE_FOR_BOOST: 28,
    MIN_RELEVANCE_TO_KEEP: 12,
    STRONG_MATCH_THRESHOLD: 48,

    /* Nearest brand / product-name match (lenient vs typo correction). */
    SIMILARITY_MIN: 0.58,
    SIMILARITY_STRONG: 0.78,
    SIMILARITY_MAX_MATCHES: 6,

    TITLE_MATCH_WEIGHT: 100,
    BRAND_MATCH_WEIGHT: 86,
    CATEGORY_MATCH_WEIGHT: 74,
    PRODUCT_TYPE_MATCH_WEIGHT: 70,
    COLLECTION_MATCH_WEIGHT: 62,
    ATTRIBUTE_MATCH_WEIGHT: 48,
    COLOR_MATCH_WEIGHT: 42,
    GENDER_MATCH_WEIGHT: 38,
    STYLE_MATCH_WEIGHT: 34,
    TAG_MATCH_WEIGHT: 26,
    SYNONYM_MATCH_WEIGHT: 22,
    PARTIAL_MATCH_WEIGHT: 16,
    FUZZY_MATCH_WEIGHT: 8,
    BOOST_WEIGHT: 18,
    AVAILABILITY_WEIGHT: 6,
    SHOPIFY_POSITION_WEIGHT: 4,

    GENDER_MISMATCH_PENALTY: 55,
    BRAND_MISMATCH_PENALTY: 48,
    COLOR_MISMATCH_PENALTY: 32,
    CATEGORY_MISMATCH_PENALTY: 40,

    PREDICTIVE_MAX_PRODUCTS: 8,
    SEARCH_PAGE_RERANK_LIMIT: 48,

    STOPWORDS: ['for', 'the', 'a', 'an', 'of', 'with', 'and', 'in', 'on', 'to', 'from', 'by', 'or'],

    ABBREVIATIONS: {
      wmns: 'womens',
      wmn: 'women',
      wom: 'women',
      mns: 'mens',
      menswear: 'men',
      womenswear: 'women',
      kidswear: 'kids',
      tee: 'tshirt',
      tees: 'tshirt',
      jns: 'jeans',
      sneks: 'sneakers',
      kicks: 'sneakers',
      fw: 'footwear',
      ss: 'ss',
    },

    COMPOUNDS: {
      't shirt': 'tshirt',
      't shirts': 'tshirt',
      'tee shirt': 'tshirt',
      'tee shirts': 'tshirt',
      'tshirt': 'tshirt',
      'tshirts': 'tshirt',
      'hand bag': 'handbag',
      'hand bags': 'handbag',
      'handbag': 'handbag',
      'handbags': 'handbag',
      'sweat shirt': 'hoodie',
      'sweat shirts': 'hoodie',
      'co ord': 'coord',
      'co ords': 'coord',
      'coord set': 'coord',
      'co-ord': 'coord',
      'crop top': 'croptop',
      'crop tops': 'croptop',
      'air force': 'airforce',
      'new balance': 'newbalance',
      'h and m': 'hm',
    },

    /**
     * Canonical term -> aliases.
     * The engine matches any alias and rewrites to the canonical form.
     */
    SYNONYMS: {
      tshirt: ['t shirt', 't-shirt', 'tshirts', 't-shirts', 'tee', 'tees', 'tee shirt', 'tee shirts', 'teeshirt'],
      shirt: ['shirts', 'button down', 'button-down', 'oxford'],
      shoes: ['shoe', 'footwear', 'foot wear'],
      sneakers: ['sneaker', 'trainers', 'trainer', 'kicks', 'kick', 'sports shoes', 'running shoes'],
      handbag: ['hand bag', 'handbags', 'hand bags', 'purse', 'purses', 'tote', 'totes'],
      bag: ['bags'],
      jeans: ['jean', 'denim', 'denims', 'denim jeans'],
      hoodie: ['hoodies', 'sweatshirt', 'sweatshirts', 'sweat shirt', 'sweat shirts'],
      women: ['womens', "women's", 'woman', "woman's", 'ladies', 'lady', 'female', 'womenswear', 'wmns'],
      men: ['mens', "men's", 'man', "man's", 'male', 'gents', 'gent', 'menswear'],
      kids: ['kid', 'children', 'child', 'boys', 'boy', 'girls', 'girl'],
      dress: ['dresses', 'frock', 'frocks', 'gown', 'gowns'],
      top: ['tops', 'blouse', 'blouses'],
      jacket: ['jackets', 'coat', 'coats', 'blazer', 'blazers'],
      kurta: ['kurtas', 'kurti', 'kurtis'],
      saree: ['sarees', 'sari', 'saris'],
      coord: ['co-ord', 'co ord', 'co-ords', 'coord set', 'co-ord set', 'coords'],
      oversized: ['over sized', 'oversize', 'oversizes', 'relaxed fit'],
      casual: ['everyday', 'daily wear'],
      formal: ['office', 'office wear', 'formals'],
      party: ['partywear', 'party wear', 'evening'],
      ethnic: ['ethnic wear', 'traditional', 'festive'],
      western: ['western wear'],
      sports: ['sport', 'athleisure', 'activewear', 'active wear', 'gym'],
      running: ['run', 'runner'],
      slim: ['slim fit'],
      regular: ['regular fit'],
      streetwear: ['street wear', 'street'],
      sandals: ['sandal', 'flip flops', 'flipflops', 'slides'],
      heels: ['heel', 'high heels'],
      boots: ['boot', 'ankle boots'],
      trousers: ['trouser', 'pants', 'pant', 'chinos', 'chino'],
      shorts: ['short'],
      skirt: ['skirts'],
      sweater: ['sweaters', 'jumper', 'jumpers', 'pullover'],
      cardigan: ['cardigans'],
      polo: ['polos', 'polo shirt', 'polo tshirt'],
      crop: ['cropped', 'crop top', 'croptop'],
      jewellery: ['jewelry', 'jewelery', 'jewels', 'necklace', 'earrings'],
      watch: ['watches'],
      sunglasses: ['sunglass', 'shades'],
      perfume: ['perfumes', 'fragrance', 'fragrances', 'cologne', 'edt', 'edp'],
      makeup: ['make up', 'cosmetics', 'cosmetic'],
      skincare: ['skin care', 'moisturizer', 'serum'],
    },

    GENDERS: {
      men: ['men', 'mens', 'man', 'male', 'gents', 'gent', 'boys', 'boy'],
      women: ['women', 'womens', 'woman', 'female', 'ladies', 'lady', 'girls', 'girl'],
      kids: ['kids', 'kid', 'children', 'child'],
    },

    COLORS: [
      'black', 'white', 'red', 'blue', 'green', 'pink', 'brown', 'beige', 'cream',
      'navy', 'grey', 'gray', 'maroon', 'olive', 'orange', 'yellow', 'purple',
      'lavender', 'mustard', 'gold', 'silver', 'nude', 'offwhite', 'off-white',
      'khaki', 'teal', 'coral', 'burgundy', 'charcoal', 'ivory', 'peach', 'mint',
    ],

    CATEGORIES: [
      'shoes', 'sneakers', 'sandals', 'heels', 'boots', 'loafers', 'slippers', 'footwear',
      'tshirt', 'shirt', 'top', 'blouse', 'polo', 'kurta', 'kurti', 'tunic',
      'jeans', 'trousers', 'pants', 'shorts', 'skirt', 'leggings', 'joggers',
      'dress', 'gown', 'jumpsuit', 'coord', 'saree', 'lehenga',
      'hoodie', 'sweatshirt', 'sweater', 'cardigan', 'jacket', 'coat', 'blazer',
      'handbag', 'bag', 'backpack', 'wallet', 'belt',
      'watch', 'sunglasses', 'jewellery', 'cap', 'hat', 'scarf',
      'perfume', 'makeup', 'skincare', 'lipstick',
    ],

    CATEGORY_FAMILIES: {
      shoes: 'footwear',
      sneakers: 'footwear',
      sandals: 'footwear',
      heels: 'footwear',
      boots: 'footwear',
      loafers: 'footwear',
      slippers: 'footwear',
      footwear: 'footwear',
      tshirt: 'tops',
      shirt: 'tops',
      top: 'tops',
      blouse: 'tops',
      polo: 'tops',
      kurta: 'tops',
      kurti: 'tops',
      tunic: 'tops',
      crop: 'tops',
      jeans: 'bottoms',
      trousers: 'bottoms',
      pants: 'bottoms',
      shorts: 'bottoms',
      skirt: 'bottoms',
      leggings: 'bottoms',
      joggers: 'bottoms',
      dress: 'dresses',
      gown: 'dresses',
      jumpsuit: 'dresses',
      coord: 'dresses',
      hoodie: 'outerwear',
      sweatshirt: 'outerwear',
      sweater: 'outerwear',
      cardigan: 'outerwear',
      jacket: 'outerwear',
      coat: 'outerwear',
      blazer: 'outerwear',
      handbag: 'bags',
      bag: 'bags',
      backpack: 'bags',
      saree: 'ethnic',
      lehenga: 'ethnic',
    },

    STYLES: [
      'casual', 'formal', 'party', 'oversized', 'slim', 'regular', 'streetwear',
      'ethnic', 'western', 'sports', 'running', 'athleisure', 'vintage', 'retro',
      'minimal', 'printed', 'solid', 'striped', 'checked', 'floral', 'graphic',
      'cropped', 'relaxed', 'skinny', 'wide', 'straight', 'bootcut',
    ],

    SIZES: ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', 'free size'],

    MATERIALS: [
      'cotton', 'linen', 'silk', 'wool', 'leather', 'denim', 'polyester',
      'rayon', 'viscose', 'chiffon', 'georgette', 'crepe', 'suede', 'knit',
    ],

    OCCASIONS: ['party', 'wedding', 'office', 'casual', 'festive', 'beach', 'gym', 'travel'],

    /**
     * Seed brands for close-name matching (e.g. napchie → napchief).
     * Collection titles/handles from skipp-search-catalog.liquid are merged on the search page.
     */
    BRANDS: [
      'nike', 'adidas', 'puma', 'reebok', 'new balance', 'newbalance', 'asics', 'skechers',
      'converse', 'vans', 'fila', 'under armour', 'h&m', 'hm', "levi's", 'levis',
      'zara', 'uniqlo', 'gap', 'forever 21', 'only', 'vero moda', 'jack & jones',
      'roadster', 'hrx', 'wrogn', 'us polo', 'tommy hilfiger', 'calvin klein',
      'ralph lauren', 'lacoste', 'gucci', 'prada', 'louis vuitton', 'dior',
      'hush puppies', 'clarks', 'bata', 'crocs', 'woodland',
      'lords and kings', 'street junkies', 'cop underdog',
      'napchief',
    ],

    COMMON_SEARCH_TERMS: [
      'nike air', 'air force', 'air max', 'ultraboost', 'samba', 'gazelle',
      'oversized tshirt', 'white sneakers', 'black dress', 'party wear',
      'casual shoes', 'denim jacket', 'co ord set',
    ],
  };

  root.SKIPP_SEARCH_CONFIG = SKIPP_SEARCH_CONFIG;

  if (typeof module === 'object' && module.exports) {
    module.exports = SKIPP_SEARCH_CONFIG;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this);
